import { Component, h, Prop, State, Watch, Element } from "@stencil/core";

interface Column {
  field: string;
  header: string;
}

interface FilterValue {
  value: any;
  condition?: string;
  type?: any;
  field?: string;
}

interface DataSource {
  [key: string]: any;
  id: number | string;
}

@Component({
  tag: "signet-datagrid",
  styleUrl: "signet-datagrid.scss",
  shadow: false,
})
export class SignetDataGrid {
  @Prop() grid_id!: string;
  @Prop({ mutable: true }) dataSource: DataSource[] = [];
  @Prop() columns: Column[] = [];

  @State() internalData: DataSource[] = [];
  @State() filterValues: { [key: string]: FilterValue } = {};
  filters: { field: string; type?: string }[] = [];
  private filterListener!: (event: CustomEvent) => void;

  @Element() el!: HTMLElement;

  @Watch("dataSource")
  watchDataSource(newValue: DataSource[]) {
    this.internalData = [...newValue];
  }

  componentDidLoad() {
    this.filterListener = (event: CustomEvent) => {
      if (event.detail.grid_id === this.grid_id) {
        this.handleFilterChange(event.detail);
        8;
      }
    };
    document.addEventListener("filterChanged", this.filterListener);
  }

  // componentDidUnload() {
  //   document.removeEventListener("filterChanged", this.filterListener);
  // }

  handleFilterChange({
    field,
    value,
    condition,
    type,
  }: {
    field: string;
    value: any;
    condition?: string;
    type?: string;
  }) {
    this.filterValues = {
      ...this.filterValues,
      [field]: { value, condition, type },
    };

    this.applyFilters();

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: this.internalData,
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: this.internalData,
    }));
  }

  applyFilters() {
    this.internalData = this.dataSource.filter((item) => {
      return Object.entries(this.filterValues).every(([key]) => {
        const filterObj = this.filterValues[key];
        const filterType = filterObj?.type;
        const itemValue =
          typeof item[key] === "string" ? item[key].toLowerCase() : item[key];
        const filterValue =
          typeof filterObj.value === "string"
            ? filterObj.value.toLowerCase()
            : filterObj.value;

        // console.log("🔍 Key:", key, "item: ", item,  "FilterObject:", filterObj, "itemValue:", itemValue, "FilterValue:", filterValue, "filterCondition:", filterCondition);

        // ✅ Skip filtering if the filter value is undefined, null, or empty
        if (
          !filterObj ||
          filterObj.value === undefined ||
          filterObj.value === null ||
          filterObj.value === ""
        ) {
          return true;
        }

        // ✅ Text Filtering (Case-insensitive)
        if (filterType === "datagrid-search-text-input") {
          //   console.log("🔍 Text Filtering:", itemValue, filterValue);

          return Object.values(item).some((val) => {
            if (typeof val === "string" || typeof val === "number") {
              return val.toString().toLowerCase().includes(filterValue);
            }
            return false;
          });
        }

        // ✅ Text Filtering (Case-insensitive)
        if (filterType === "datagrid-filter-text-input") {
          // console.log("🔍 Text Filtering:", itemValue, filterValue);
          return itemValue.includes(filterValue);
        }

        // ✅ Dropdown Filtering (Exact match)
        if (filterType === "datagrid-filter-dropdown") {
          return itemValue === filterValue;
        }

        // ✅ Dropdown-Logic Filtering (Numerical comparisons)
        if (filterType === "dropdown-logic") {
          if (key.includes(",")) {
            const fields = key.split(",");

            const field1 = fields[0].trim();
            const field2 = fields[1].trim();
            const field3 = fields[2].trim();

            const itemValue1 = item[field1];
            const itemValue2 = item[field2];
            const itemValue3 = item[field3];

            switch (filterObj.condition) {
              case ">":
                return (
                  itemValue1 > filterValue ||
                  itemValue2 > filterValue ||
                  itemValue3 > filterValue
                );
              case "<":
                return (
                  itemValue1 < filterValue ||
                  itemValue2 < filterValue ||
                  itemValue3 < filterValue
                );
              case ">=":
                return (
                  itemValue1 >= filterValue ||
                  itemValue2 >= filterValue ||
                  itemValue3 >= filterValue
                );
              case "<=":
                return (
                  itemValue1 <= filterValue ||
                  itemValue2 <= filterValue ||
                  itemValue3 <= filterValue
                );
              case "=":
                return (
                  itemValue1 === filterValue ||
                  itemValue2 === filterValue ||
                  itemValue3 === filterValue
                );
              default:
                return true;
            }
          } else {
            if (isNaN(itemValue) || isNaN(filterValue)) return false;

            switch (filterObj.condition) {
              case ">":
                return itemValue > filterValue;
              case "<":
                return itemValue < filterValue;
              case ">=":
                return itemValue >= filterValue;
              case "<=":
                return itemValue <= filterValue;
              case "=":
                return itemValue === filterValue;
              case "!=":
                return itemValue !== filterValue;
              default:
                console.log("Unknown condition:", filterObj.condition);
                return true;
            }
          }
        }

        // ✅ DateRange Filtering
        if (filterType === "datagrid-filter-date-range") {
          if (key.includes(",")) {
            const fields = key.split(",");

            const field1 = fields[0].trim();
            const field2 = fields[1].trim();

            const startDate = new Date(item[field1]);
            const endDate = new Date(item[field2]);

            const filterValueStartDate = new Date(filterValue?.startDate);
            const filterValueEndDate = new Date(filterValue?.endDate);

            return (
              startDate >= filterValueStartDate && endDate <= filterValueEndDate
            );
          }
        }

        // ✅ Checkbox Filtering (Allows multiple selections)
        if (filterType === "datagrid-filter-checkbox") {
          if (!Array.isArray(filterValue)) return true;

          return filterValue.includes(itemValue);
        }

        // ✅ Checkbox Filtering (Allows multiple selections)
        if (filterType === "datagrid-filter-checkbox-eligible-year") {
          if (!Array.isArray(filterValue)) return true;

          // Checkbox Filtering Churn Status
          if (key.includes(",")) {
            const fields = key.split(",");

            const field1 = fields[0].trim();
            const field2 = fields[1].trim();
            const field3 = fields[2].trim();

            const itemValue1 = item[field1];
            const itemValue2 = item[field2];
            const itemValue3 = item[field3];

            return filterValue.some(
              (status) =>
                itemValue1 === status ||
                itemValue2 === status ||
                itemValue3 === status
            );
          } else {
            // Checkbox Filtering Eligible Years
            return filterValue.some((value) => item[value]);
          }
        }

        // ✅ Boolean Filtering (Checks for true, false, or all)
        if (filterType === "boolean") {
          if (filterValue === null) return true; // If "All" is selected, return true
          return itemValue === filterValue; // Match exact boolean value
        }

        return true;
      });
    });
  }

  render() {
    return (
      <div class="signet-datagrid-container">
        <table>
          <thead>
            <tr>
              {this.columns.map((col) => (
                <th key={col.field}>{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {this.internalData.length ? (
              this.internalData.map((row, rowIndex) => (
                <tr key={row.id || rowIndex}>
                  {this.columns.map((col) => (
                    <td key={col.field}>{row[col.field] ?? "-"}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={this.columns.length} class="no-data">
                  No Data Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }
}
